/**
 * End-to-end encryption utilities using TweetNaCl
 *
 * Encryption Strategy:
 * 1. User creates account -> generate X25519 keypair
 * 2. Private key encrypted with password-derived key (PBKDF2)
 * 3. Encrypted private key stored on server
 * 4. On login, decrypt private key in browser
 * 5. Use keypair for encrypting/decrypting user data
 */

import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64, encodeUTF8, decodeUTF8 } from 'tweetnacl-util';

// PBKDF2 parameters (secure defaults)
const PBKDF2_ITERATIONS = 100000;
const SALT_LENGTH = 16;
const KEY_LENGTH = 32;

// Key derivation from password using PBKDF2 (Web Crypto API)
async function deriveKeyFromPassword(password: string, salt: Uint8Array): Promise<Uint8Array> {
  const encoder = new TextEncoder();

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );

  // Create a new ArrayBuffer from salt to satisfy TypeScript
  const saltBuffer = new Uint8Array(salt).buffer;

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltBuffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    KEY_LENGTH * 8
  );

  return new Uint8Array(derivedBits);
}

// Generate a new keypair for E2E encryption
export function generateKeyPair(): { publicKey: string; secretKey: Uint8Array } {
  const keyPair = nacl.box.keyPair();
  return {
    publicKey: encodeBase64(keyPair.publicKey),
    secretKey: keyPair.secretKey,
  };
}

// Encrypt the private key with password-derived key
export async function encryptPrivateKey(
  secretKey: Uint8Array,
  password: string
): Promise<string> {
  // Generate random salt for key derivation
  const salt = nacl.randomBytes(SALT_LENGTH);
  const derivedKey = await deriveKeyFromPassword(password, salt);

  // Generate random nonce for encryption
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);

  // Encrypt the secret key
  const encrypted = nacl.secretbox(secretKey, nonce, derivedKey);

  // Combine salt + nonce + encrypted data
  const combined = new Uint8Array(salt.length + nonce.length + encrypted.length);
  combined.set(salt, 0);
  combined.set(nonce, salt.length);
  combined.set(encrypted, salt.length + nonce.length);

  return encodeBase64(combined);
}

// Decrypt the private key with password
export async function decryptPrivateKey(
  encryptedKey: string,
  password: string
): Promise<Uint8Array | null> {
  try {
    const combined = decodeBase64(encryptedKey);

    // Extract salt, nonce, and encrypted data
    const salt = combined.slice(0, SALT_LENGTH);
    const nonce = combined.slice(SALT_LENGTH, SALT_LENGTH + nacl.secretbox.nonceLength);
    const encrypted = combined.slice(SALT_LENGTH + nacl.secretbox.nonceLength);

    // Derive key from password
    const derivedKey = await deriveKeyFromPassword(password, salt);

    // Decrypt
    const secretKey = nacl.secretbox.open(encrypted, nonce, derivedKey);

    return secretKey || null;
  } catch {
    return null;
  }
}

// Encrypt data for storage (using secret key directly - for user's own data)
export function encryptData(data: string, secretKey: Uint8Array): string {
  // Generate nonce
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);

  // Derive a 32-byte key for secretbox from the full secret key
  // (box.secretKey is 32 bytes, same as secretbox key length)
  const encryptionKey = secretKey.slice(0, nacl.secretbox.keyLength);

  // Encrypt
  const messageBytes = decodeUTF8(data);
  const encrypted = nacl.secretbox(messageBytes, nonce, encryptionKey);

  // Combine nonce + encrypted data
  const combined = new Uint8Array(nonce.length + encrypted.length);
  combined.set(nonce, 0);
  combined.set(encrypted, nonce.length);

  return encodeBase64(combined);
}

// Decrypt data from storage
export function decryptData(encryptedData: string, secretKey: Uint8Array): string | null {
  try {
    const combined = decodeBase64(encryptedData);

    // Extract nonce and encrypted data
    const nonce = combined.slice(0, nacl.secretbox.nonceLength);
    const encrypted = combined.slice(nacl.secretbox.nonceLength);

    // Derive encryption key
    const encryptionKey = secretKey.slice(0, nacl.secretbox.keyLength);

    // Decrypt
    const decrypted = nacl.secretbox.open(encrypted, nonce, encryptionKey);

    if (!decrypted) {
      return null;
    }

    return encodeUTF8(decrypted);
  } catch {
    return null;
  }
}

// Hash data (for cache invalidation)
export async function hashData(data: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = new Uint8Array(hashBuffer);
  return Array.from(hashArray, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// Encrypt file (for resume uploads)
export function encryptFile(fileData: ArrayBuffer, secretKey: Uint8Array): string {
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const encryptionKey = secretKey.slice(0, nacl.secretbox.keyLength);

  const messageBytes = new Uint8Array(fileData);
  const encrypted = nacl.secretbox(messageBytes, nonce, encryptionKey);

  const combined = new Uint8Array(nonce.length + encrypted.length);
  combined.set(nonce, 0);
  combined.set(encrypted, nonce.length);

  return encodeBase64(combined);
}

// Decrypt file
export function decryptFile(encryptedData: string, secretKey: Uint8Array): ArrayBuffer | null {
  try {
    const combined = decodeBase64(encryptedData);

    const nonce = combined.slice(0, nacl.secretbox.nonceLength);
    const encrypted = combined.slice(nacl.secretbox.nonceLength);
    const encryptionKey = secretKey.slice(0, nacl.secretbox.keyLength);

    const decrypted = nacl.secretbox.open(encrypted, nonce, encryptionKey);

    if (!decrypted) {
      return null;
    }

    // Create a proper ArrayBuffer copy
    return decrypted.buffer.slice(decrypted.byteOffset, decrypted.byteOffset + decrypted.byteLength) as ArrayBuffer;
  } catch {
    return null;
  }
}

// Store secret key in session storage (cleared when tab closes)
const SECRET_KEY_STORAGE = 'silentjobhunter_secret_key';

export function storeSecretKey(secretKey: Uint8Array): void {
  sessionStorage.setItem(SECRET_KEY_STORAGE, encodeBase64(secretKey));
}

export function getSecretKey(): Uint8Array | null {
  const stored = sessionStorage.getItem(SECRET_KEY_STORAGE);
  if (!stored) return null;
  return decodeBase64(stored);
}

export function clearSecretKey(): void {
  sessionStorage.removeItem(SECRET_KEY_STORAGE);
}
